import type { Authorization, Ownership } from '@skop-app/lib-domain/types'

type ProjectLike = {
  id?: string | null
  organizationID?: string | null
  progress?: string | null
  isGuest?: boolean | string | null
}

export type ProjectPermissions = {
  isGuestAccount: boolean
  isExternalProject: boolean
  isArchivedProject: boolean
  isReadonly: boolean
  canEditProject: boolean
  canDuplicateProject: boolean
  canArchiveProject: boolean
  canManageMembers: boolean
  canLeaveProject: boolean
  canEditCatalog: boolean
  canEditBlueprint: boolean
  canManageAlerts: boolean
}

export type ProjectReadonlyMessageKey =
  | 'project-guest-readonly'
  | 'project-external-readonly'
  | 'project-archived-readonly'

type ProjectReadonlyReasonPermissions = Pick<
  ProjectPermissions,
  'isGuestAccount' | 'isExternalProject' | 'isArchivedProject'
>

type ResolveProjectPermissionsParams = {
  authorization?: Authorization
  ownership?: Ownership
  activeOrganizationId?: string
  project?: ProjectLike | null
}

export type ProjectMemberPermissions = {
  isCurrentUser: boolean
  canEditMember: boolean
  canRemoveMember: boolean
  removeActionLabel: 'Quitter' | 'Retirer'
}

type ResolveProjectMemberPermissionsParams = {
  projectPermissions: Pick<
    ProjectPermissions,
    'canLeaveProject' | 'canManageMembers'
  >
  currentUserId?: string | null
  memberUserId?: string | null
}

export const resolveProjectPermissions = ({
  authorization,
  ownership,
  activeOrganizationId,
  project,
}: ResolveProjectPermissionsParams): ProjectPermissions => {
  const isGuestAccount = authorization?.user?.role === 'GUEST'
  const canCreateProjects = Boolean(authorization?.projectsAuth?.canCreate)
  const canUpdateProjects = Boolean(authorization?.projectsAuth?.canUpdate)
  const canDeleteProjects = Boolean(authorization?.projectsAuth?.canDelete)
  const canInviteTeamMember = Boolean(authorization?.canInviteTeamMember)
  const projectOrganizationId = project?.organizationID
  const resolvedActiveOrganizationId =
    activeOrganizationId ?? ownership?.organizationID ?? undefined
  const isProjectGuest = Boolean(project?.isGuest) && !isGuestAccount
  const isExternalProject =
    Boolean(
      projectOrganizationId &&
      resolvedActiveOrganizationId !== projectOrganizationId,
    ) || isProjectGuest
  const isArchivedProject = project?.progress === 'done'
  const isReadonly = isGuestAccount || isExternalProject || isArchivedProject
  const canMutateProject =
    Boolean(project?.id) && !isReadonly && canUpdateProjects
  const canDuplicateProject =
    Boolean(project?.id) &&
    !isReadonly &&
    canCreateProjects &&
    canUpdateProjects
  const canToggleArchive =
    Boolean(project?.id) &&
    !isGuestAccount &&
    !isExternalProject &&
    canDeleteProjects &&
    (project?.progress === 'active' || project?.progress === 'done')
  const canCreateProject = !project?.id && !isGuestAccount && canCreateProjects

  return {
    isGuestAccount,
    isExternalProject,
    isArchivedProject,
    isReadonly,
    canEditProject: canCreateProject || canMutateProject,
    canDuplicateProject,
    canArchiveProject: canToggleArchive,
    canManageMembers: canMutateProject && canInviteTeamMember,
    canLeaveProject:
      Boolean(project?.id) && isExternalProject && !isGuestAccount,
    canEditCatalog: canMutateProject,
    canEditBlueprint: canMutateProject,
    canManageAlerts: canMutateProject,
  }
}

export const resolveProjectMemberPermissions = ({
  projectPermissions,
  currentUserId,
  memberUserId,
}: ResolveProjectMemberPermissionsParams): ProjectMemberPermissions => {
  const isCurrentUser = Boolean(
    currentUserId && memberUserId && currentUserId === memberUserId,
  )
  const canEditMember = projectPermissions.canManageMembers
  const canRemoveMember =
    canEditMember || (isCurrentUser && projectPermissions.canLeaveProject)

  return {
    isCurrentUser,
    canEditMember,
    canRemoveMember,
    removeActionLabel: isCurrentUser ? 'Quitter' : 'Retirer',
  }
}

export const getProjectReadonlyMessageKey = (
  projectPermissions: ProjectReadonlyReasonPermissions,
): ProjectReadonlyMessageKey | undefined => {
  if (projectPermissions.isGuestAccount) {
    return 'project-guest-readonly'
  }

  if (projectPermissions.isExternalProject) {
    return 'project-external-readonly'
  }

  if (projectPermissions.isArchivedProject) {
    return 'project-archived-readonly'
  }

  return undefined
}
